import React, { useState } from "react";
import _ from "lodash";
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import ArticleIcon from "@mui/icons-material/Article";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import { RowData, SetSelect } from "../types";
import { 
  downloadSingleInvoice, 
  createPDFPreviewUrl, 
  downloadAllInvoices,
  formatInvoiceDate,
  getInvoiceNumber
} from "./helper";

type Props = {
  rowsData: RowData[],
  setSelect: SetSelect
}

/**
 * Component for displaying and downloading invoice PDFs
 */
function DownloadInvoiceTable({ rowsData, setSelect }: Props) {
  // State hooks
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadingStatus, setDownloadingStatus] = useState<Record<string, string | null>>({});
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState<Record<string, boolean>>({});
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<RowData | null>(null);
  
  /**
   * Handles downloading a single invoice
   */
  const handleDownloadInvoice = async (rowData: RowData) => {
    try {
      setDownloadingStatus(prev => ({ ...prev, [rowData.id]: "loading" }));
      await downloadSingleInvoice(rowData);
      setDownloadingStatus(prev => ({ ...prev, [rowData.id]: "completed" }));
      
      // Reset the status after 3 seconds
      setTimeout(() => {
        setDownloadingStatus(prev => ({ ...prev, [rowData.id]: null }));
      }, 3000);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      setDownloadingStatus(prev => ({ ...prev, [rowData.id]: null }));
    }
  };
  
  /**
   * Handles previewing an invoice
   */
  const handlePreviewInvoice = async (rowData: RowData) => {
    try {
      setIsPreviewLoading(prev => ({ ...prev, [rowData.id]: true }));
      setSelectedInvoice(rowData);
      
      const url = await createPDFPreviewUrl(rowData);
      setPreviewUrl(url);
      
      // Open the dialog
      setPreviewDialogOpen(true);
      setIsPreviewLoading(prev => ({ ...prev, [rowData.id]: false }));
    } catch (error) {
      console.error("Error previewing invoice:", error);
      setIsPreviewLoading(prev => ({ ...prev, [rowData.id]: false }));
    }
  };
  
  /**
   * Handles closing the preview dialog
   */
  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    // Cleanup the URL after the dialog is closed
    setTimeout(() => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }, 300);
  };
  
  /**
   * Handles downloading all invoices as a ZIP
   */
  const handleDownloadAllInvoices = async () => {
    if (!rowsData || rowsData.length === 0) return;
    
    setIsDownloadingAll(true);
    
    try {
      await downloadAllInvoices(rowsData);
    } catch (error) {
      console.error("Error downloading all invoices:", error);
    } finally {
      setIsDownloadingAll(false);
    }
  };
  
  /**
   * Renders the empty state when no invoices are available
   */
  const renderEmptyState = () => (
    <Grid item xs={12}>
      <Box 
        sx={{ 
          textAlign: 'center',
          p: 4,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          bgcolor: '#f8f8f8'
        }}
      >
        <ArticleIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
        <Typography color="text.secondary">No invoice data available</Typography>
      </Box>
    </Grid>
  );
  
  /**
   * Renders a single invoice card
   */
  const renderInvoiceCard = (rowData: RowData) => (
    <Grid item xs={12} key={rowData.id}>
      <Card variant="outlined" sx={{ 
        '&:hover': { boxShadow: 3 }, 
        transition: 'box-shadow 0.3s',
        borderLeft: '4px solid #1976D2'
      }}>
        <CardContent>
          <Grid container alignItems="center">
            <Grid item xs>
              <Typography variant="h6" component="div" fontWeight="500">
                {rowData.expert_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoice #{getInvoiceNumber(rowData)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {formatInvoiceDate(rowData.requested_on)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Call ID: {rowData.callIds}
              </Typography>
            </Grid>
            <Grid item>
              <CardActions sx={{ gap: 1 }}>
                {/* Preview Button */}
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={isPreviewLoading[rowData.id] ? 
                    <CircularProgress size={20} color="inherit" /> : 
                    <VisibilityIcon />
                  }
                  onClick={() => handlePreviewInvoice(rowData)}
                  disabled={isPreviewLoading[rowData.id] || downloadingStatus[rowData.id] === "loading"}
                  sx={{ minWidth: '120px' }}
                >
                  {isPreviewLoading[rowData.id] ? "Loading..." : "Preview"}
                </Button>
                
                {/* Download Button */}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={downloadingStatus[rowData.id] === "loading" ? 
                    <CircularProgress size={20} color="inherit" /> : 
                    downloadingStatus[rowData.id] === "completed" ? 
                      <FileDownloadDoneIcon /> : 
                      <DownloadIcon />
                  }
                  onClick={() => handleDownloadInvoice(rowData)}
                  disabled={downloadingStatus[rowData.id] === "loading" || isPreviewLoading[rowData.id]}
                  sx={{ minWidth: '160px' }}
                >
                  {downloadingStatus[rowData.id] === "loading" ? "Generating..." : 
                    downloadingStatus[rowData.id] === "completed" ? "Downloaded" : "Download Invoice"}
                </Button>
              </CardActions>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
  
  /**
   * Renders the PDF preview dialog
   */
  const renderPreviewDialog = () => (
    <Dialog
      open={previewDialogOpen}
      onClose={handleClosePreview}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          m: 1
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box display="flex" alignItems="center">
          <PictureAsPdfIcon sx={{ mr: 1, color: '#d32f2f' }} />
          <Typography variant="h6" component="div">
            {selectedInvoice ? `Invoice Preview - ${selectedInvoice.expert_name}` : 'Invoice Preview'}
          </Typography>
        </Box>
        <IconButton onClick={handleClosePreview} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        {previewUrl && (
          <iframe
            src={previewUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title="Invoice Preview"
          />
        )}
      </DialogContent>
      
      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleClosePreview} variant="outlined">
          Close
        </Button>
        {selectedInvoice && (
          <Button 
            onClick={() => {
              handleClosePreview();
              handleDownloadInvoice(selectedInvoice);
            }}
            variant="contained" 
            color="primary"
            startIcon={<DownloadIcon />}
          >
            Download This Invoice
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2" fontWeight="bold" color="primary">
          Invoice Downloads
        </Typography>
        
        {rowsData.length > 1 && (
          <Button
            variant="contained"
            color="success"
            startIcon={isDownloadingAll ? <CircularProgress size={20} color="inherit" /> : <FileDownloadDoneIcon />}
            onClick={handleDownloadAllInvoices}
            disabled={isDownloadingAll}
          >
            {isDownloadingAll ? "Preparing..." : "Download All Invoices"}
          </Button>
        )}
      </Box>
      
      <Grid container spacing={2}>
        {rowsData.length === 0 ? renderEmptyState() : rowsData.map(renderInvoiceCard)}
      </Grid>
      
      {renderPreviewDialog()}
    </Container>
  );
}

export default DownloadInvoiceTable;